import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Icon, IconsType, IconSizesType } from "@/comn/components";

const WIDTHS = {
    1: "w-1/12",
    2: "w-2/12",
    3: "w-3/12",
    4: "w-4/12",
    5: "w-5/12",
    6: "w-6/12",
    7: "w-7/12",
    8: "w-8/12",
    9: "w-9/12",
    10: "w-10/12",
    11: "w-11/12",
    12: "w-full",
    fit: "w-fit",
    full: "w-full",
};

const HEIGHTS = {
    sm: "h-7",
    lg: "h-10",
};

const BUTTON_STYLES = {
    "button-contained": {
        primary: "bg-primary border-primary text-primary-contrast",
        "primary-contrast": "bg-primary-contrast text-primary-contrast",
        black: "bg-black border-black text-white",
        white: "bg-white border-white text-black",
        darkgray: "bg-darkgray border-darkgray text-white",
        gray: "bg-gray border-gray text-white",
        lightgray: "bg-lightgray border-lightgray text-white",
        blue: "bg-blue border-blue text-white",
        success: "bg-green border-green text-white",
        error: "bg-error border-error text-white",
        info: "bg-info border-info text-white",
        warning: "bg-warning border-warning text-black",
    },
    "button-outlined": {
        primary: "border-primary text-primary-text",
        "primary-contrast": "border-primary-contrast text-primary-contrast",
        black: "border-black text-black",
        white: "border-white text-white",
        darkgray: "border-darkgray text-darkgray",
        gray: "border-gray text-gray dark:text-white",
        lightgray: "border-lightgray text-lightgray",
        blue: "border-blue text-blue",
        success: "border-green text-green",
        error: "border-error text-error",
        info: "border-info text-info",
        warning: "border-warning text-black dark:text-warning",
    },
    "button-underlined": {
        primary: "text-primary-text",
        "primary-contrast": "text-primary-contrast",
        black: "text-black",
        white: "text-white",
        darkgray: "text-darkgray",
        gray: "text-gray",
        lightgray: "text-lightgray",
        blue: "text-blue",
        success: "text-success",
        error: "text-error",
        info: "text-info",
        warning: "text-warning",
    },
};

export const BUTTON_VARIANTS = {
    contained: { variant: "button-contained", color: "primary" },
    outlined: { variant: "button-outlined", color: "primary" },
    underlined: { variant: "button-underlined", color: "primary" },

    primary: { variant: "button-contained", color: "primary" },
    secondary: { variant: "button-contained", color: "gray" },
    warning: { variant: "button-contained", color: "warning" },
    danger: { variant: "button-contained", color: "error" },
    info: { variant: "button-contained", color: "info" },

    "outline-primary": { variant: "button-outlined", color: "primary" },
    "outline-secondary": { variant: "button-outlined", color: "gray" },
    "outline-warning": { variant: "button-outlined", color: "warning" },
    "outline-danger": { variant: "button-outlined", color: "error" },
    "outline-info": { variant: "button-outlined", color: "info" },
    "outline-bg": { variant: "button-outlined", color: "" },
};

export const BUTTON_ROLES = {
    save: { text: "B_SAVE", color: "primary", variant: "button-contained", icon: undefined },
    list: { text: "B_LST", color: "gray", variant: "button-contained", icon: undefined },
    submit: { text: "B_SBMT", color: "warning", variant: "button-contained", icon: undefined },
    search: { text: "B_SRCH", color: "blue", variant: "button-contained", icon: undefined },
    close: { text: "B_CLS", color: "error", variant: "button-contained", icon: undefined },
    add: { text: "B_ADD", color: "blue", variant: "button-contained", icon: undefined },
    delete: { text: "B_DEL", color: "error", variant: "button-contained", icon: undefined },
    reset: { text: "B_RESET", color: "warning", variant: "button-contained", icon: undefined },
    confirm: { text: "B_CFRM", color: "success", variant: "button-contained", icon: undefined },
    ok: { text: "B_OK", color: "primary", variant: "button-contained", icon: undefined },
    edit: { text: "B_EDIT", color: "primary", variant: "button-contained", icon: undefined },
    new: { text: "B_NEW", color: "gray", variant: "button-contained", icon: undefined },
    cancel: { text: "B_CNCL", color: "warning", variant: "button-contained", icon: undefined },
    apply: { text: "B_APPD", color: "blue", variant: "button-contained", icon: undefined },
    gridAdd: { text: "B_ADD", color: "blue", variant: "button-outlined", icon: undefined },
    gridDelete: { text: "B_DELETE", color: "error", variant: "button-outlined", icon: undefined },
    excelDown: { text: "B_EXC_DWNL", color: "info", variant: "button-outlined", icon: "bars_arrow_down" },
    excelDownServer: { text: "B_EXC_DWNL", color: "info", variant: "button-outlined", icon: "cloud_arrow_down" },
    tmplDown: { text: "B_TMPL_DWNL", color: "info", variant: "button-outlined", icon: undefined },
    excelUpload: { text: "B_EXC_UPLD", color: "info", variant: "button-outlined", icon: undefined },
};

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    width?: keyof typeof WIDTHS;
    height?: keyof typeof HEIGHTS;
    variant?: keyof typeof BUTTON_VARIANTS;
    color?:
        | "primary"
        | "primary-contrast"
        | "black"
        | "white"
        | "darkgray"
        | "gray"
        | "lightgray"
        | "blue"
        | "success"
        | "error"
        | "info"
        | "warning";
    role?: keyof typeof BUTTON_ROLES;
    icon?: IconsType;
    size?: IconSizesType;
    isLoading?: boolean;
};

export const Button = (props: ButtonProps) => {
    let {
        children,
        type = "button",
        variant = "contained",
        color,
        width,
        height,
        role,
        icon,
        size = "xs",
        isLoading,
        onClick,
        hidden,
        ...rest
    } = props;

    const { t } = useTranslation();

    const handleClick = (event: any) => {
        if (!onClick) return;
        if (isLoading) return;
        onClick(event);
    };

    // 색상 우선순위
    // props -> role -> variant
    let buttonContent;
    if (typeof children === "string") {
        buttonContent = t(children);
    } else if (role) {
        buttonContent = t(BUTTON_ROLES[role].text);
    } else {
        buttonContent = children;
    }

    let buttonVariant = "";
    if (role) {
        buttonVariant = BUTTON_ROLES[role].variant;
    } else if (variant) {
        buttonVariant = BUTTON_VARIANTS[variant].variant;
    }

    let buttonColor = "";
    if (color) {
        buttonColor = (BUTTON_STYLES as any)[buttonVariant][color];
    } else if (role) {
        buttonColor = (BUTTON_STYLES as any)[buttonVariant][BUTTON_ROLES[role].color];
    } else if (variant) {
        buttonColor = (BUTTON_STYLES as any)[buttonVariant][BUTTON_VARIANTS[variant].color];
    }

    let buttonIcon = (icon || (role && BUTTON_ROLES[role].icon) || false) as any;

    return (
        <button
            {...rest}
            hidden={hidden}
            type={type}
            onClick={handleClick}
            className={classNames(
                buttonVariant,
                buttonColor,
                width && WIDTHS[width],
                height && HEIGHTS[height],
                isLoading && "bg-lightgray border-lightgray",
                hidden && "hidden",
            )}
        >
            {isLoading && (
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <Icon icon="loading" className="animate-spin" />
                </div>
            )}
            {buttonIcon && <Icon icon={buttonIcon} size={size} className="mr-1" />}

            {buttonContent}
        </button>
    );
};
